import { SocialType, SocialTypeId } from "./Social.model";

/**
 * Detects the SocialTypeId from a URL using a two-pass strategy:
 *  1. Domain match — check the URL's bare hostname against each SocialType's domain list.
 *  2. Keyword match — check the URL's path segments against each SocialType's keyword list.
 *     This catches types like BLOG (/blog), CAREERS (/jobs), PORTFOLIO (/portfolio) on generic domains.
 *
 * Returns the matching SocialTypeId, WEBSITE as a fallback for any valid URL
 * that doesn't match a known platform, or null if the URL isn't parseable yet.
 */
export function detectSocialTypeIdFromUrl(
  url: string,
  availableTypes: SocialType[]
): SocialTypeId | null {
  if (!url || url.trim().length === 0) return null;

  // Normalise — add a protocol if missing so URL() doesn't throw
  let normalised = url.trim();
  if (!normalised.startsWith("http://") && !normalised.startsWith("https://")) {
    normalised = "https://" + normalised;
  }

  let parsed: URL;
  try {
    parsed = new URL(normalised);
  } catch {
    // Not a valid URL yet — leave the selector as-is
    return null;
  }

  // Strip leading "www." so "www.linkedin.com" matches "linkedin.com"
  const bare = parsed.hostname.replace(/^www\./, "");

  // Pass 1 — Domain match
  for (const socialType of availableTypes) {
    if (
      socialType.domains?.some(
        (domain) => bare === domain || bare.endsWith("." + domain)
      )
    ) {
      return socialType.id;
    }
  }

  // Pass 2 — Keyword match against URL path segments
  // Split path into individual segments and normalise to lowercase
  const pathSegments = parsed.pathname
    .toLowerCase()
    .split("/")
    .filter(Boolean);

  for (const socialType of availableTypes) {
    if (!socialType.keywords || socialType.keywords.length === 0) continue;
    if (socialType.keywords.some((kw) => pathSegments.includes(kw))) {
      return socialType.id;
    }
  }

  // Valid URL but no known platform matched
  return SocialTypeId.WEBSITE;
}


/**
 * Finds the full SocialType object matching a detected SocialTypeId,
 * falling back to OTHER if the detected id is not in the available list.
 */
export function resolveSocialType(
  detectedId: SocialTypeId,
  availableTypes: SocialType[]
): SocialType | null {
  return (
    availableTypes.find((t) => t.id === detectedId) ??
    availableTypes.find((t) => t.id === SocialTypeId.OTHER) ??
    null
  );
}
